import { useState } from 'react';
import type { StaticImageData } from 'next/image';
import NextImage from 'next/image';
import { CMSLink } from '@/components/cms-link';
import type { CMSLinkType } from '@/components/cms-link';
import { cn } from '@/utils/classname';
import * as Dialog from '@radix-ui/react-dialog';
import { ExternalLink, Maximize2, X } from 'lucide-react';

import type { Props } from './types';

export const PostImage: React.FC<Props> = (props) => {
  const width: number = props.width ?? 0;
  const height: number | undefined = props.height ?? 0;
  const alt: string = props.resource?.alt ?? '';
  const src: StaticImageData | string =
    props.resource?.url ??
    `${process.env.NEXT_PUBLIC_CMS_URL}/media/${props.resource?.filename}`;
  const aspectRatio: string = props.resource?.aspectRatio ?? '';
  const link: CMSLinkType | undefined = props.link;
  const onLoadFromProps: (() => void) | undefined = props.onLoad;
  const imageSizes: string = props.sizes ?? '';
  const imgClassName: string = props.className ?? '';
  const breakpoints = {
    s: 768,
    m: 1024,
    l: 1440,
  };
  const priority: boolean = props.priority ?? false;

  const [isLoading, setIsLoading] = useState(true);

  // NOTE: this is used by the browser to determine which image to download at different screen sizes
  const sizes =
    imageSizes ||
    Object.entries(breakpoints)
      .map(([, value]) => `(max-width: ${value}px) ${value}px`)
      .join(', ');

  // Function to convert aspectRatio string to usable class
  const getAspectRatioClass = (ratio: string | undefined) => {
    if (ratio) {
      const [widthRatio, heightRatio] = ratio.split('/').map(Number);
      return `aspect-${widthRatio}/${heightRatio}`;
    }
    return '';
  };

  return (
    <>
      <div
        className={cn(
          getAspectRatioClass(aspectRatio),
          aspectRatio === '2/5' || aspectRatio === '9/16'
            ? 'relative mx-auto md:w-[60%] lg:w-[45%]'
            : 'relative',
        )}
      >
        <Dialog.Root>
          <Dialog.Trigger asChild>
            <NextImage
              className={cn(
                isLoading && 'bg-slate-50',
                'h-full w-full cursor-zoom-in object-cover',
                imgClassName,
              )}
              unoptimized
              src={src}
              alt={alt}
              onLoad={() => {
                setIsLoading(false);
                if (typeof onLoadFromProps === 'function') {
                  onLoadFromProps();
                }
              }}
              width={width}
              height={height}
              sizes={sizes}
              priority={priority}
            />
          </Dialog.Trigger>

          {link ? (
            <>
              <CMSLink {...link}>
                <button
                  type="button"
                  className="hover:bg-offWhite absolute right-2.5 top-2.5 hidden items-center rounded-md border border-[#E5E7EB] bg-white px-2 py-2 text-black shadow-sm lg:inline-flex"
                >
                  <ExternalLink className="h-5 w-5" />
                </button>
              </CMSLink>
              <div
                className="absolute right-2.5 top-2.5 inline-flex lg:hidden"
                role="group"
              >
                <Dialog.Trigger asChild>
                  <button
                    type="button"
                    className="hover:bg-offWhite items-center rounded-s-md border-b border-l border-t border-[#E5E7EB] bg-white px-2 py-2 text-black"
                  >
                    <Maximize2 className="h-3 w-3 md:h-5 md:w-5" />
                  </button>
                </Dialog.Trigger>
                <CMSLink {...link}>
                  <button
                    type="button"
                    className="hover:bg-offWhite items-center rounded-e-md border border-[#E5E7EB] bg-white px-2 py-2 text-black"
                  >
                    <ExternalLink className="h-3 w-3 md:h-5 md:w-5" />
                  </button>
                </CMSLink>
              </div>
            </>
          ) : (
            <Dialog.Trigger asChild>
              <button
                type="button"
                className="hover:bg-offWhite absolute right-2.5 top-2.5 items-center rounded-md border border-[#E5E7EB] bg-white px-2 py-2 text-black lg:hidden"
              >
                <Maximize2 className="h-3 w-3 md:h-5 md:w-5" />
              </button>
            </Dialog.Trigger>
          )}
          <Dialog.Portal>
            <Dialog.Overlay className="fixed inset-0 z-50 bg-black/50" />
            <Dialog.Content className="fixed inset-0 z-[60] flex items-center justify-center p-4">
              <div className="h-full w-full">
                <div
                  className={cn(
                    getAspectRatioClass(aspectRatio),
                    'relative left-1/2 top-1/2 max-h-full max-w-full -translate-x-1/2 -translate-y-1/2 transform bg-white shadow-lg',
                  )}
                >
                  <NextImage
                    className={cn(
                      isLoading && 'bg-slate-50',
                      'h-full w-full object-cover',
                      imgClassName,
                    )}
                    unoptimized
                    src={src}
                    alt={alt}
                    onLoad={() => {
                      setIsLoading(false);
                      if (typeof onLoadFromProps === 'function') {
                        onLoadFromProps();
                      }
                    }}
                    width={width}
                    height={height}
                    sizes={sizes}
                    priority={priority}
                  />
                </div>
              </div>
              <Dialog.Close asChild>
                <button
                  className="fixed right-4 top-4 p-2 text-white"
                  aria-label="Close"
                >
                  <X />
                </button>
              </Dialog.Close>
            </Dialog.Content>
          </Dialog.Portal>
        </Dialog.Root>
      </div>
    </>
  );
};
